import type { ICONS } from './icons';
import { Container } from '@acies/core';
import Button from './button';
import ButtonEvent from './button-event';
import InputField from './input-field';
import InputFieldEvent from './input-field-event';
import InputPopupSelectorEvent from './input-popup-selector-event';

/** The InputPopupSelector combines the possibility of entering a value in a text input field og clicking a menu button for a popup with extended selection / filtering etc. */
export default class InputPopupSelector extends Container {
	public static override observedAttributes = [...Container.observedAttributes, 'text', 'text-align', 'type'];
	/**
	 * The decimals property sets if a numeric InputPopupSelector accepts a decimal point. It has no effect when type is 'text'.
	 *
	 * @param {boolean} value True if a decimal point is accepted or false. default is true
	 */
	public set decimals(value: boolean) {
		this.#inputFieldChild.decimals = value;
	}

	public get decimals(): boolean {
		return this.#inputFieldChild.decimals;
	}

	/**
	 * The enabled property sets if the InputField is interactive or not.
	 *
	 * @param {boolean} value True if the InputField is interactive or false. default is true
	 */
	public set enabled(value: boolean) {
		this.#inputFieldChild.enabled = value;
		this.#buttonChild.enabled = value;
	}

	public get enabled(): boolean {
		return this.#inputFieldChild.enabled && this.#buttonChild.enabled;
	}

	/**
	 * The icon is shown to the left and is helpful if you want to add further visual information.
	 *
	 * @param value The name of the icon you want to show.
	 */
	public set icon(value: keyof typeof ICONS | null) {
		this.#inputFieldChild.icon = value;
	}

	public get icon(): keyof typeof ICONS | null {
		return this.#inputFieldChild.icon;
	}

	/**
	 * The negatives property sets if a numeric InputPopupSelector accepts a leading minus sign. It has no effect when type is 'text'.
	 *
	 * @param {boolean} value True if negative numbers are accepted or false. default is false
	 */
	public set negatives(value: boolean) {
		this.#inputFieldChild.negatives = value;
	}

	public get negatives(): boolean {
		return this.#inputFieldChild.negatives;
	}

	/** The text content in the child InputField Component. */
	public set text(value: string) {
		this.#inputFieldChild.text = value;
	}

	public get text(): string {
		return this.#inputFieldChild.text;
	}

	/**
	 * The text-align CSS property sets the horizontal alignment of the inline-level content inside a block element. This means it works like vertical-align but in the horizontal direction.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/text-align} for syntax
	 */
	public set textAlign(value: 'center' | 'left' | 'right') {
		assertIsValidTextAlign(value);
		this.#inputFieldChild.textAlign = value;
	}

	public get textAlign(): 'center' | 'left' | 'right' {
		return this.#inputFieldChild.textAlign;
	}

	/**
	 * The type property is used when you want to enforce numeric validation for the text property or user input.
	 *
	 * If the type is set to 'numeric', validation of the values will be enforced, otherwise not.
	 */
	public set type(value: 'numeric' | 'text') {
		assertIsValidType(value);
		this.#inputFieldChild.type = value;
	}

	public get type(): 'numeric' | 'text' {
		return this.#inputFieldChild.type;
	}

	#buttonChild: Button;
	#inputFieldChild: InputField;

	public constructor() {
		super();
		this.height = 32;
		this.#inputFieldChild = new InputField();
		this.#inputFieldChild.width = 'fill';
		this.#inputFieldChild.cornerRadius = [8, 0, 0, 8];
		this.#inputFieldChild.height = 'fill';
		this.#buttonChild = new Button();
		this.#buttonChild.height = 'fill';
		this.#buttonChild.icon = 'dots-horizontal';
		this.#buttonChild.cornerRadius = [0, 8, 8, 0];
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'text') {
			this.#textAttributeChanged(value);
		} else if (name === 'type') {
			this.#typeAttributeChanged(value);
		} else if (name === 'text-align') {
			this.#textAlignAttributeChanged(value);
		}
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.#inputFieldChild, this.#buttonChild]);
		this.#addEventListeners();
	}

	#addEventListeners(): void {
		this.addEventListener(InputFieldEvent.BLURRED, this.#eventsHandler as EventListener);
		this.addEventListener(InputFieldEvent.CHANGED, this.#eventsHandler as EventListener);
		this.addEventListener(InputFieldEvent.FOCUSED, this.#eventsHandler as EventListener);
		this.addEventListener(InputFieldEvent.HOVERED, this.#eventsHandler as EventListener);
		this.addEventListener(InputFieldEvent.UN_HOVERED, this.#eventsHandler as EventListener);
		this.addEventListener(ButtonEvent.CLICKED, this.#eventsHandler as EventListener);
	}

	#eventsHandler(event: ButtonEvent | InputFieldEvent): void {
		event.stopPropagation();
		if (event instanceof ButtonEvent) {
			this.#handleButtonEvent();
		} else if (event instanceof InputFieldEvent) {
			this.#handleInputEvent(event);
			this.#updateZIndex(event);
		}
	}

	#handleButtonEvent(): void {
		this.dispatchEvent(new InputPopupSelectorEvent(InputPopupSelectorEvent.CLICKED, this.text));
	}

	#handleInputEvent(event: InputFieldEvent): void {
		if (event.type === InputFieldEvent.BLURRED) {
			this.dispatchEvent(new InputPopupSelectorEvent(InputPopupSelectorEvent.BLURRED, this.text));
		} else if (event.type === InputFieldEvent.CHANGED) {
			this.dispatchEvent(new InputPopupSelectorEvent(InputPopupSelectorEvent.CHANGED, this.text));
		} else if (event.type === InputFieldEvent.FOCUSED) {
			this.dispatchEvent(new InputPopupSelectorEvent(InputPopupSelectorEvent.FOCUSED, this.text));
		}
	}

	#textAlignAttributeChanged(value: string): void {
		assertIsValidTextAlign(value);
		this.textAlign = value;
	}

	#textAttributeChanged(value: string): void {
		this.text = value;
	}

	#typeAttributeChanged(value: string): void {
		assertIsValidType(value);
		this.type = value;
	}

	/**
	 * Lifts the InputField above the Button while it has the focus, so its focus stroke is drawn over the edge they share.
	 *
	 * Only the focus moves it. Hovering is not styled, so there is no stroke under the pointer to lift, and a field that is typed in or left by the pointer while focused keeps its place on top.
	 */
	#updateZIndex(event: InputFieldEvent): void {
		if (event.type === InputFieldEvent.FOCUSED) {
			this.#inputFieldChild.zIndex = 1;
		} else if (event.type === InputFieldEvent.BLURRED) {
			this.#inputFieldChild.zIndex = 'auto';
		}
	}
}
customElements.define('ac-input-popup-selector', InputPopupSelector);

function assertIsValidTextAlign(value: unknown): asserts value is 'center' | 'left' | 'right' {
	if (value === 'center' || value === 'left' || value === 'right') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "left", "center" or "right"`);
}

function assertIsValidType(value: unknown): asserts value is 'numeric' | 'text' {
	if (value === 'numeric' || value === 'text') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be "numeric" or "text"`);
}
